import React, { useState, useMemo } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import MiscUtils from 'services/MiscUtils';
import CalendarUtils from 'services/CalendarUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { DefaultCheckbox } from 'services/UIUtils';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import moment from 'moment-timezone';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';

const FACTORS = {
  Male: {
    age: 3.06117,
    totalCholesterol: 1.1237,
    hdl: 0.93263,
    avgRisk: 23.9802,
    riskPeriod: 0.88936,
    cig: {
      true: 0.65451,
      false: 0,
    },
    hbpMed: {
      true: 1.99881,
      false: 1.93303,
    },
    diabetes: {
      true: 0.57367,
      false: 0,
    },
  },
  Female: {
    age: 2.32888,
    totalCholesterol: 1.20904,
    hdl: 0.70833,
    avgRisk: 26.1931,
    riskPeriod: 0.95012,
    cig: {
      true: 0.52873,
      false: 0,
    },
    hbpMed: {
      true: 2.82263,
      false: 2.76157,
    },
    diabetes: {
      true: 0.69154,
      false: 0,
    },
  },
};

const DEFAULT_ARRAY = [];
const MG_DL_CONVERT_FACTOR = 38.67;
const NAME = 'FRS';

// Strict shape match — keep in sync with server isFRSPayload. Free-form
// notes whose name happens to be 'FRS' won't parse and are ignored, so the
// modal can't accidentally clobber legacy free-text notes on save and the
// CPP rail won't render JSON for them.
function tryParseFRS(rawNotes) {
  if (typeof rawNotes !== 'string' || rawNotes[0] !== '{') return null;
  let p;
  try {
    p = JSON.parse(rawNotes);
  } catch {
    return null;
  }
  if (!p || p.v !== 1) return null;
  if (typeof p.result !== 'string') return null;
  if (p.gender !== 'Male' && p.gender !== 'Female') return null;
  if (typeof p.totalCholesterol !== 'number' || typeof p.hdl !== 'number') return null;
  if (typeof p.systolicBP !== 'number') return null;
  if (
    typeof p.medication !== 'boolean' ||
    typeof p.cig !== 'boolean' ||
    typeof p.diabetes !== 'boolean'
  )
    return null;
  if (typeof p.calculatedAt !== 'number') return null;
  if (p.source !== 'manual' && p.source !== 'auto-lab') return null;
  return p;
}

function decodeNotes(rawNotes) {
  const parsed = tryParseFRS(rawNotes);
  if (!parsed) return null;
  const when = CalendarUtils.format(parsed.calculatedAt, 'YYYY/MM/DD');
  const isAuto = parsed.source === 'auto-lab';
  const headline = `FRS ${parsed.result}%${isAuto ? ' (auto)' : ''}`;
  const detailJsx = (
    <span>
      TC {parsed.totalCholesterol} / HDL {parsed.hdl} mmol/L
      <br />
      SBP {parsed.systolicBP}
      {parsed.medication ? ' (on med)' : ''}
      {parsed.cig ? ', smoker' : ''}
      {parsed.diabetes ? ', DM' : ''}
    </span>
  );
  return { parsed, when, isAuto, headline, detailJsx };
}

export const FRSCalculatorUtils = {
  NAME,
  tryParseFRS,
  decodeNotes,
  decodeForDisplay(item) {
    const decoded = decodeNotes(item?.value?.notes);
    if (!decoded) return null;
    return {
      text: decoded.headline,
      notes: (
        <span>
          {decoded.detailJsx}
          <br />
          {decoded.isAuto ? 'Auto-updated' : 'Calculated'} {decoded.when}
          {decoded.isAuto && ' — review toggles'}
        </span>
      ),
    };
  },
};

// https://www.merckmanuals.com/medical-calculators/Framingham08.htm
export const FRSCalculatorModal = ({ patient, onSubmit, initialPayload }) => {
  const dispatch = useAppDispatch();
  const patientHr = useAppSelector((state) => state.healthRecords.byId[patient.id]);
  const patientSummary = patientHr?.dataSummary || DEFAULT_ARRAY;

  const { defaultHDLCholesterol, defaultTotalCholesterol, defaultSystolicBP } = useMemo(() => {
    let defaultHDLCholesterol = patientSummary.find(
      ({ metric, origin }) => metric.id === 'HDL CHOLESTEROL' && origin === 'Lab',
    )?.last_value?.value1;

    let defaultTotalCholesterol = patientSummary.find(
      ({ metric, origin }) => metric.id === 'CHOLESTEROL' && !metric.type && origin === 'Lab',
    )?.last_value?.value1;

    let defaultSystolicBP = patientSummary.find(
      ({ metric, origin }) => metric.id === 'bp' && origin === 'Practitioner',
    )?.last_value?.value1;

    return { defaultHDLCholesterol, defaultTotalCholesterol, defaultSystolicBP };
  }, [patientSummary]);

  // Strict-match prior FRS Private Note. Free-form notes named 'FRS' are
  // ignored — we won't reuse their id and won't clobber them on save.
  const previousFRS = useMemo(() => {
    const notes = patientHr?.healthRecords?.notes ?? [];
    for (const n of notes) {
      if (n?.value?.name !== NAME) continue;
      if (n?.history?.[0]?.action === 'Removed') continue;
      const parsed = tryParseFRS(n?.value?.notes);
      if (parsed) return { id: n.id, ...parsed };
    }
    return null;
  }, [patientHr]);

  // When invoked from CPP edit, initialPayload is the exact saved snapshot —
  // restore it verbatim so toggles/labs match the saved attempt instead of
  // being overwritten by fresher lab values.
  const editing = !!initialPayload;

  const [gender, setGender] = useState(
    initialPayload?.gender ?? previousFRS?.gender ?? patient.gender,
  );
  const [age, setAge] = useState(
    initialPayload?.age ?? CalendarUtils.calculateAgeNumber(patient.date_of_birth),
  );
  // Lab values: when editing, restore the saved snapshot. Otherwise prefer
  // fresh dataSummary; fall back to prior FRS values only when no lab present.
  const [totalCholesterol, setTotalCholesterol] = useState(
    editing
      ? initialPayload.totalCholesterol
      : defaultTotalCholesterol ?? previousFRS?.totalCholesterol,
  );
  const [hdl, setHdl] = useState(
    editing ? initialPayload.hdl : defaultHDLCholesterol ?? previousFRS?.hdl,
  );
  const [systolicBP, setSystolicBP] = useState(
    editing ? initialPayload.systolicBP : defaultSystolicBP ?? previousFRS?.systolicBP,
  );
  // Toggles: prior FRS only. These cannot be inferred from labs.
  const [medication, setMedication] = useState(
    initialPayload?.medication ?? previousFRS?.medication ?? false,
  );
  const [cig, setCig] = useState(initialPayload?.cig ?? previousFRS?.cig ?? false);
  const [diabetes, setDiabetes] = useState(
    initialPayload?.diabetes ?? previousFRS?.diabetes ?? false,
  );
  const [saving, setSaving] = useState(false);

  const isLoadingMetrics = useIsLoading(api.HealthRecords.addHealthData);

  const factors = FACTORS[gender];

  const riskFactors =
    Math.log(parseInt(age)) * factors.age +
    Math.log(parseFloat(totalCholesterol) * MG_DL_CONVERT_FACTOR) * factors.totalCholesterol -
    Math.log(parseFloat(hdl) * MG_DL_CONVERT_FACTOR) * factors.hdl +
    Math.log(parseInt(systolicBP)) * factors.hbpMed['' + medication] +
    factors.cig['' + cig] +
    factors.diabetes['' + diabetes] -
    factors.avgRisk;

  const result = (100.0 * (1 - Math.pow(factors.riskPeriod, Math.exp(riskFactors)))).toFixed(2);

  function getResult() {
    let riskFactorsText = `Age: ${age}\nTotal Cholesterol: ${totalCholesterol}\nHDL: ${hdl}\nSystolic BP: ${systolicBP}\nHypertension Medication: ${
      medication ? 'Yes' : 'No'
    }\nCigarrete Smoker: ${cig ? 'Yes' : 'No'}\nDiabetes: ${diabetes ? 'Yes' : 'No'}`;
    let text = `Framingham Risk Score (FRS): ${result}%\nRisk Factors\n${riskFactorsText}`;
    return text;
  }

  async function copyToCliboard() {
    const text = getResult();
    await navigator.clipboard.writeText(text);
  }

  async function save() {
    setSaving(true);
    try {
      const calculatedAt = moment().unix();
      const payload = {
        v: 1,
        result,
        age: parseInt(age),
        gender,
        totalCholesterol: parseFloat(totalCholesterol),
        hdl: parseFloat(hdl),
        systolicBP: parseInt(systolicBP),
        medication: !!medication,
        cig: !!cig,
        diabetes: !!diabetes,
        calculatedAt,
        source: 'manual',
      };
      const noteData = { value: { name: NAME, notes: JSON.stringify(payload) } };
      // Reuse id only when the prior note parsed as a valid FRS payload —
      // tryParseFRS in previousFRS already enforces that.
      if (previousFRS?.id) noteData.id = previousFRS.id;

      // Sequential await — partial-success is observable to staff this way:
      // a metrics-side failure surfaces a toast before the note write fires.
      const metricResponse = await dispatch(
        api.HealthRecords.addHealthData({
          patientId: patient.id,
          healthData: { name: 'frs', values: [{ date: calculatedAt, value: result }] },
        }),
        { errorTitle: 'FRS' },
      );
      if (!metricResponse) return;

      const noteResponse = await dispatch(
        api.HealthRecords.addOrEditHealthRecordPatientIssue({
          patientId: patient.id,
          field: 'notes',
          patientIssues: [noteData],
        }),
        { errorTitle: 'FRS' },
      );
      if (!noteResponse) return;

      MiscUtils.showToastrInfo({
        title: 'FRS',
        message: 'Saved to metrics and Private Notes.',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal">
      <div className="title">Framingham Risk Score Calculator</div>
      <div className="padded-box">
        <div className="row">
          <DefaultTextField
            name="age"
            label="Age"
            value={age}
            onChange={(event) => setAge(event.target.value)}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultSelectField
            label="Gender"
            name="gender"
            value={gender}
            onChange={(event) => setGender(event.target.value)}
            inputs={['Male', 'Female']}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="total_cholesterol"
            label="Total Cholesterol (mmol/L)"
            autoFocus
            value={totalCholesterol}
            onChange={(event) => setTotalCholesterol(event.target.value)}
            style={{ width: 'calc(100% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="hdl_cholesterol"
            label="HDL Cholesterol (mmol/L)"
            value={hdl}
            onChange={(event) => setHdl(event.target.value)}
            style={{ width: 'calc(100% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="systolic_bp"
            label="Systolic BP (mmHg)"
            value={systolicBP}
            onChange={(event) => setSystolicBP(event.target.value)}
            style={{ width: 'calc(100% - 15px)', marginRight: '15px' }}
          />
        </div>
        <div className="row" style={{ marginTop: '10px' }}>
          <DefaultCheckbox
            name="medication"
            value={medication}
            onChange={() => setMedication(!medication)}
            text="Hypertension medication"
          />
        </div>
        <div className="row" style={{ marginTop: '5px' }}>
          <DefaultCheckbox
            name="cigarette"
            value={cig}
            onChange={() => setCig(!cig)}
            text="Cigarette smoker"
          />
        </div>
        <div className="row" style={{ marginTop: '5px' }}>
          <DefaultCheckbox
            name="diabetes"
            value={diabetes}
            onChange={() => setDiabetes(!diabetes)}
            text="Diabetes present"
          />
        </div>
        <div className="row">
          <DefaultTextField
            name="risk_factors"
            label="Risk Factors"
            value={isNaN(riskFactors) ? '' : riskFactors.toFixed(2)}
            disabled={true}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
          <DefaultTextField
            name="result"
            label="Risk"
            value={isNaN(result) ? '' : result + '%'}
            disabled={true}
            style={{ width: 'calc(50% - 15px)', marginRight: '15px' }}
          />
        </div>
        <DefaultButton
          name="Save"
          icon="floppy-disk"
          onClick={save}
          style={{ width: '100%', marginTop: '10px' }}
          disabled={isNaN(result)}
          loading={saving || isLoadingMetrics}
        />
        <DefaultButton
          name={onSubmit ? 'Submit' : 'Copy to Clipboard'}
          icon={onSubmit ? undefined : 'clipboard'}
          onClick={onSubmit ? () => onSubmit(getResult()) : copyToCliboard}
          style={{ width: '100%', marginTop: '10px' }}
          disabled={isNaN(result)}
          className="blue"
        />
      </div>
    </div>
  );
};

export default FRSCalculatorModal;
